/**
 * Maps weather provider condition codes to a closed vocabulary.
 *
 * Closed vocabulary: clear, sunny, partly cloudy, cloudy, rain, drizzle, storm, snow, fog, windy, unknown
 */

const CONDITION_MAP = {
  // Clear/Sunny conditions
  'clear': 'clear',
  'sunny': 'sunny',
  'fair': 'clear',

  // Cloudy conditions
  'partly cloudy': 'partly cloudy',
  'partly_cloudy': 'partly cloudy',
  'mostly cloudy': 'cloudy',
  'cloudy': 'cloudy',
  'overcast': 'cloudy',

  // Rain conditions
  'rain': 'rain',
  'light rain': 'rain',
  'heavy rain': 'rain',
  'showers': 'rain',
  'drizzle': 'drizzle',
  'light drizzle': 'drizzle',

  // Storm conditions
  'thunderstorm': 'storm',
  'storm': 'storm',
  'thunder': 'storm',
  'lightning': 'storm',

  // Snow conditions
  'snow': 'snow',
  'light snow': 'snow',
  'heavy snow': 'snow',
  'flurries': 'snow',
  'sleet': 'snow',

  // Fog conditions
  'fog': 'fog',
  'mist': 'fog',
  'haze': 'fog',

  // Wind conditions
  'windy': 'windy',
}

/**
 * Maps a weather provider's condition string to the canonical vocabulary.
 * @param {string} providerCondition - The condition string from the weather provider
 * @returns {string} One of: clear, sunny, partly cloudy, cloudy, rain, drizzle, storm, snow, fog, windy, unknown
 */
export function mapWeatherCondition(providerCondition) {
  if (!providerCondition || typeof providerCondition !== 'string') {
    return 'unknown'
  }

  const normalized = providerCondition.toLowerCase().trim()

  // Direct match
  if (CONDITION_MAP[normalized]) {
    return CONDITION_MAP[normalized]
  }

  // Partial match (e.g., "light rain showers" contains "rain")
  for (const [key, value] of Object.entries(CONDITION_MAP)) {
    if (normalized.includes(key)) {
      return value
    }
  }

  return 'unknown'
}

/**
 * Creates a weather snapshot object from provider weather data.
 * @param {object} contextWeather - The context_weather object from the provider
 * @returns {object|null} Weather snapshot with {temperature_f, temperature_unit, condition, humidity_pct, captured_at} or null on failure
 */
export function createWeatherSnapshot(contextWeather) {
  if (!contextWeather || typeof contextWeather !== 'object') {
    return null
  }

  const { temp_c, condition, humidity_pct, fetched_at } = contextWeather

  // Validate required temperature - the only truly required field for partial display
  if (!Number.isFinite(temp_c)) {
    return null
  }

  // Convert Celsius to Fahrenheit
  const temperature_f = Math.round((temp_c * 9) / 5 + 32)

  // Map condition to vocabulary when present
  const mappedCondition = condition ? mapWeatherCondition(condition) : null

  return {
    temperature_f,
    temperature_unit: 'F',
    condition: mappedCondition,
    humidity_pct: Number.isFinite(humidity_pct) ? Math.round(humidity_pct) : null,
    captured_at: fetched_at || null,
  }
}

/**
 * Formats a weather snapshot for display.
 * @param {object} snapshot - The weather snapshot object
 * @returns {string} Formatted string like "58°F · rain · 82%" or "58°F · rain" or "58°F" or empty string if no temperature
 */
export function formatWeatherSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    return ''
  }

  const { temperature_f, temperature_unit, condition, humidity_pct } = snapshot

  // Snapshots are stored in Fahrenheit; anything else is malformed and must not
  // render (e.g. "71°undefined") or block the context_weather fallback.
  if (!Number.isFinite(temperature_f) || temperature_unit !== 'F') {
    return ''
  }

  const parts = [`${temperature_f}°F`]

  if (condition) {
    const label = condition.charAt(0).toUpperCase() + condition.slice(1)
    parts.push(label)
  }

  if (Number.isFinite(humidity_pct)) {
    parts.push(`${humidity_pct}% humidity`)
  }

  return parts.join(' · ')
}

/**
 * Resolve the weather to display on a wear-log card.
 *
 * FOS-WEAR-WEATHER-CARD-FALLBACK-001. `weather_snapshot` is a new column that
 * is populated only at wear-log creation, and only once its migration is
 * applied and the wear_weather flag is on. Every wear logged before that point
 * has an empty snapshot forever, so the card would stay blank on the whole
 * existing history.
 *
 * The weather is already there, though: `context_weather` is captured on every
 * wear log and is what HomePage reads for the header temperature. It carries
 * the same fields the snapshot is derived from, so a card can fall back to it
 * and show weather on historical wears with no migration at all.
 *
 * Prefer the stored snapshot when present -- it is the value captured and
 * frozen at wear time -- and derive from context_weather otherwise.
 *
 * @param {object} log - a wear-log row
 * @returns {object|null} a weather snapshot shape, or null when neither source has usable weather
 */
export function resolveWearWeather(log) {
  if (!log || typeof log !== 'object') return null
  const stored = log.weather_snapshot
  if (stored && typeof stored === 'object' && formatWeatherSnapshot(stored)) return stored
  return createWeatherSnapshot(log.context_weather)
}
